'use client';

import { formatDateAU } from '@/lib/formatters/australian-formatters';
import type { Handling } from '@/lib/services/matter-handling';

/**
 * What a handover SAYS — the handler, the firm's note on what is being done, and
 * the date it expects that to be finished by (VALE-1854).
 *
 * **Split into a title and a detail line, and shared, because its two consumers
 * have different containers.** The institution sheet stacks them in a centred
 * empty-state panel; the inventory item drawer renders a `<Banner>`, whose `line`
 * slot is a `<p>` and so can hold inline content only. Splitting here is what lets
 * both use the same words — the alternative was the Banner re-authoring the
 * sentences, which is how the two surfaces start describing one handover
 * differently.
 *
 * The item drawer previously stated none of this: it carried a chip whose tooltip
 * held the note and the date, so a reader could not see the firm's own file note
 * without hovering a badge they had no reason to think was hoverable. That is how
 * it was reported — *"Is this surfaced anywhere??"*, written beside a note the
 * product had faithfully saved.
 *
 * That note matters more than its size suggests. It is the firm's contemporaneous
 * record of what it handed over, written at the moment it did so, and on a
 * handover to the LPR it is a record of a limit on the engagement itself.
 */

/** "Priya Raghavan is handling this." — the handover in one sentence. */
export function handedOverTitle(handling: Handling): string {
  return `${handling.handledBy?.name ?? 'Someone outside the firm'} is handling this.`;
}

/**
 * The firm's note and expected-by date, as INLINE content for a `<p>`.
 *
 * Renders `null` when neither is set rather than an empty element, so a handover
 * carrying neither — a perfectly ordinary one — shows no empty supporting line
 * (EH-DR-005).
 *
 * **"The firm expects…", never a bare "Expected by…".** The date is the firm's own
 * optional note, from the hand-over dialog's "Expected to be finished by" field,
 * and nothing external is measured against it. Phrased as a bare deadline it reads
 * as one somebody imposed on the firm, which is the opposite of what it is, and a
 * reader then treats an overdue handover far more urgently than it warrants. This
 * wording was the fix for a reader asking, reasonably, what "overdue" even meant.
 */
export function HandedOverDetail({ handling }: { handling: Handling }) {
  const { handlingNote, handlingExpectedBy, handlingOverdue } = handling;
  if (!handlingNote && !handlingExpectedBy) return null;

  return (
    <>
      {handlingNote ? <span>&ldquo;{handlingNote}&rdquo;</span> : null}
      {handlingNote && handlingExpectedBy ? <span> &middot; </span> : null}
      {handlingExpectedBy ? (
        <span>
          {handlingOverdue
            ? 'The firm expected this to be finished by '
            : 'The firm expects this to be finished by '}
          {formatDateAU(handlingExpectedBy)}.
        </span>
      ) : null}
    </>
  );
}

/**
 * Both halves stacked, for a container that can hold block content — the
 * institution sheet's panel. The item drawer's `<Banner>` uses the two pieces
 * directly instead, one per slot.
 */
export function HandedOverSummary({
  handling,
  className
}: {
  handling: Handling;
  className?: string;
}) {
  if (!handling.handledBy) return null;

  return (
    <div className={className} data-testid="handed-over-summary">
      <p className="text-foreground text-sm font-medium">
        {handedOverTitle(handling)}
      </p>
      <p className="text-muted-foreground text-sm">
        <HandedOverDetail handling={handling} />
      </p>
    </div>
  );
}
